import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, UtensilsCrossed, Leaf, Wine, ShoppingBag, CalendarX2, Gift, Sparkles, Cake, Heart, Star, CalendarDays } from "lucide-react";
import { useContent, isSpecialActive, isBannerActive } from "@/lib/content";
import { Overline } from "@/components/Overline";
import { Seo } from "@/components/Seo";
import { LiveKitchen } from "@/components/LiveKitchen";

const RESERVE_URL = "https://reservierung.lacontadina-dresden.de";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease } } };

const valueMeta = [
  { icon: Leaf, key: 0, title: "Eigenes Olivenöl", text: "Natives Olivenöl aus eigener Produktion in Kampanien – probieren & kaufen bei uns." },
  { icon: UtensilsCrossed, key: 1, title: "Klassiker neu interpretiert", text: "Unsere Köche verbinden Tradition und Kreativität mit einer persönlichen Note." },
  { icon: Wine, key: 2, title: "Eine Reise nach Italien", text: "85 Plätze drinnen, 110 auf der Terrasse – mitten in Dresden-Löbtau." },
];

export default function Home() {
  const c = useContent();
  const t = c.text.home;
  const img = c.images;

  return (
    <div data-testid="home-page">
      <Seo page="home" />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center grain overflow-hidden">
        <div className="absolute inset-0">
          <video key={img.heroVideo} autoPlay muted loop playsInline preload="auto" poster={img.hero} data-testid="hero-video" className="w-full h-full object-cover">
            <source src={img.heroVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/45 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.12 } } }} className="max-w-2xl">
            <motion.span variants={fadeUp} className="block text-xs tracking-[0.35em] uppercase text-gold mb-6">{t.heroKicker}</motion.span>
            <motion.h1 variants={fadeUp} className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl text-cream leading-[0.95] tracking-tight">
              {t.heroTitle1}<br />{t.heroTitle2}<br /><span className="text-gold italic">{t.heroAccent}</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-cream/80 text-lg font-light mt-8 max-w-lg leading-relaxed">{t.heroText}</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4 mt-10">
              <a href={RESERVE_URL} target="_blank" rel="noreferrer" data-testid="hero-reservation-button" className="btn-pop bg-wine text-cream hover:bg-wine-dark px-9 py-4 uppercase text-xs tracking-[0.25em]">Tisch reservieren</a>
              <a href="https://shop.lacontadina-dresden.de/" target="_blank" rel="noreferrer" data-testid="hero-menu-button" className="btn-pop group border border-gold/60 text-cream hover:bg-gold hover:text-ink px-9 py-4 uppercase text-xs tracking-[0.25em] flex items-center gap-2">
                Online bestellen <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
          <span className="text-[10px] tracking-[0.3em] uppercase text-cream/60">Entdecken</span>
          <div className="w-6 h-10 rounded-full border border-cream/40 flex items-start justify-center p-1.5"><span className="w-1 h-2 rounded-full bg-gold animate-bounce-down" /></div>
        </motion.div>
      </section>

      {/* BETRIEBSFERIEN BANNER */}
      {isBannerActive(c.banner) && (
        <section data-testid="vacation-banner" className="bg-wine text-cream">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 text-center">
            <CalendarX2 size={26} strokeWidth={1.4} className="text-gold shrink-0 animate-pulse-soft" />
            <p className="text-sm sm:text-base">
              <span className="uppercase tracking-[0.2em] text-gold text-xs sm:text-sm mr-2">{c.banner.title}</span>
              <span className="font-light">{c.banner.text}</span>
            </p>
          </div>
        </section>
      )}

      {/* SAISON-SPEZIAL */}
      {isSpecialActive(c.special) && (
        <section data-testid="special-section" className="relative py-20 md:py-28 bg-ink text-cream grain overflow-hidden">
          {c.special.image && (
            <div className="absolute inset-0">
              <img src={c.special.image} alt={c.special.title} className="w-full h-full object-cover opacity-25 animate-kenburns-slow" />
              <div className="absolute inset-0 bg-ink/70" />
            </div>
          )}
          <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-gold mb-5"><Sparkles size={16} /> {c.special.kicker}</span>
            <h2 className="font-serif font-light text-4xl sm:text-5xl mb-5 leading-tight">{c.special.title}</h2>
            <p className="text-cream/80 font-light text-lg mb-8 max-w-xl mx-auto whitespace-pre-line">{c.special.text}</p>
            {c.special.ctaLabel && c.special.ctaUrl && (
              <a href={c.special.ctaUrl} target={c.special.ctaUrl.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid="special-cta" className="btn-pop bg-gold text-ink hover:bg-gold-dark px-9 py-4 uppercase text-xs tracking-[0.25em] inline-block">
                {c.special.ctaLabel}
              </a>
            )}
          </div>
        </section>
      )}

      {/* TAGESKARTE */}
      {c.tageskarte?.enabled && (
      <section className="py-10 bg-gold/10 border-y border-gold/20" data-testid="tageskarte-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8 text-center sm:text-left">
          <CalendarDays size={38} strokeWidth={1.3} className="text-wine shrink-0" />
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-ink mb-1">Unsere Tageskarte</h3>
            <p className="text-ink-soft font-light text-sm sm:text-base">Täglich frisch zusammengestellt – wechselnde Gerichte aus der Küche, jeden Tag neu für Sie aktualisiert.</p>
          </div>
          <a href="https://menu-manager-pro-17.emergent.host/tageskarte/la-contadina" target="_blank" rel="noreferrer" data-testid="tageskarte-btn" className="btn-pop group bg-wine text-cream hover:bg-wine-dark px-8 py-3.5 uppercase text-xs tracking-[0.25em] inline-flex items-center gap-2 whitespace-nowrap">
            Tageskarte ansehen <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>
      )}

      {/* VALUES */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {valueMeta.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: i * 0.1, ease }} className="group text-center md:text-left">
                <v.icon className="mx-auto md:mx-0 text-wine mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" size={32} strokeWidth={1.3} />
                <h3 className="font-serif text-2xl text-ink mb-3">{v.title}</h3>
                <p className="text-ink-soft font-light leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE AUS DER KÜCHE */}
      <LiveKitchen />

      {/* ABOUT SPLIT */}
      <section className="py-24 md:py-32 bg-sand grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }} className="relative group">
            <div className="overflow-hidden"><img src={img.aboutWine} alt="Unsere Küche" className="w-full h-[520px] object-cover animate-kenburns-slow" /></div>
            <img src={c.brand.logo} alt="logo" className="hidden md:block absolute -bottom-10 -right-8 h-32 w-32 rounded-full object-cover ring-4 ring-cream shadow-2xl animate-float" />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }}>
            <Overline>Über uns</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink leading-tight mb-6">{t.aboutTitle}</h2>
            <p className="text-ink-soft font-light leading-relaxed mb-5">{t.aboutP1}</p>
            <p className="text-ink-soft font-light leading-relaxed mb-8">{t.aboutP2}</p>
            <Link to="/ueber-uns" data-testid="home-about-link" className="group inline-flex items-center gap-2 text-wine uppercase text-xs tracking-[0.25em] border-b border-wine/30 pb-1 hover:border-wine transition-colors">
              Unsere Geschichte lesen <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ONLINE SHOP */}
      <section className="py-24 md:py-32 bg-cream" data-testid="shop-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <ShoppingBag className="mx-auto text-wine mb-6 animate-float" size={40} strokeWidth={1.3} />
          <Overline className="!flex justify-center">La Contadina to go</Overline>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink mb-6">Unser Online-Shop</h2>
          <p className="text-ink-soft font-light text-lg leading-relaxed mb-4">
            Lust auf italienischen Genuss zu Hause? In unserem Online-Shop bestellen Sie ganz bequem Ihre Lieblingsgerichte – frische Pizza, hausgemachte Pasta und mehr – zur Abholung.
          </p>
          <p className="text-ink-soft font-light leading-relaxed mb-10">
            Stöbern Sie durch unsere komplette Speisekarte und stellen Sie sich Ihr Menü ganz nach Geschmack zusammen.
          </p>
          <a href="https://shop.lacontadina-dresden.de/" target="_blank" rel="noreferrer" data-testid="home-shop-btn" className="btn-pop group bg-wine text-cream hover:bg-wine-dark px-10 py-4 uppercase text-xs tracking-[0.25em] inline-flex items-center gap-2">
            Jetzt online bestellen <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      {/* TREUEKARTE / LOYALTY */}
      <section className="relative py-24 md:py-32 bg-ink text-cream grain overflow-hidden" data-testid="loyalty-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }}>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-gold mb-5"><Sparkles size={16} /> Neu · Kostenlos</span>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight mb-6">La Carta del Gusto</h2>
            <p className="text-cream/80 font-light text-lg leading-relaxed mb-8">
              Unsere digitale Treuekarte. Sammeln Sie bei jedem Besuch <span className="text-gold">Sapori</span>-Bonuspunkte und sichern Sie sich exklusive Vorteile – ganz ohne Plastikkarte.
            </p>
            <ul className="space-y-4 mb-10">
              {[
                { icon: Star, text: "Bei jedem Besuch Sapori-Punkte sammeln" },
                { icon: Cake, text: "+100 Sapori als Geschenk zu Ihrem Geburtstag" },
                { icon: Gift, text: "Exklusive Aktionen & Vorteile für Mitglieder" },
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-cream/85">
                  <f.icon size={20} className="text-gold shrink-0" strokeWidth={1.5} />
                  <span className="font-light">{f.text}</span>
                </li>
              ))}
            </ul>
            <a href="https://bonus-card.preview.emergentagent.com/register" target="_blank" rel="noreferrer" data-testid="loyalty-register-btn" className="hidden btn-pop group bg-gold text-ink hover:bg-gold-dark px-10 py-4 uppercase text-xs tracking-[0.25em] inline-flex items-center gap-2">
              Jetzt kostenlos anmelden <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }} className="flex justify-center">
            <img src="https://customer-assets.emergentagent.com/job_trattoria-home-1/artifacts/nq37mmel_e8ktndkn_file_000000004cb871f4af74401bc11e6fda.png" alt="La Carta del Gusto – Treuekarte" data-testid="loyalty-card-img" className="w-full max-w-md rounded-2xl shadow-2xl border border-gold/30 animate-float" />
          </motion.div>
        </div>
      </section>

      {/* OLIVE OIL TEASER */}
      <section className="py-24 md:py-32 bg-sand grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }}>
            <Overline>Aus eigener Produktion</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink leading-tight mb-6">{t.oliveTitle}</h2>
            <p className="text-ink-soft font-light leading-relaxed mb-5">{t.oliveText}</p>
            <p className="text-gold-dark font-serif text-2xl mb-8">{t.olivePrice}</p>
            <Link to="/olivenoel" data-testid="home-olive-link" className="group inline-flex items-center gap-2 text-wine uppercase text-xs tracking-[0.25em] border-b border-wine/30 pb-1 hover:border-wine transition-colors">
              Mehr erfahren <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }} className="overflow-hidden">
            <img src={img.oliveBottle} alt="Olivenöl" className="w-full h-[520px] object-cover animate-kenburns-slow" />
          </motion.div>
        </div>
      </section>

      {/* TAKEAWAY BANNER */}
      <section className="py-16 bg-wine text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="flex items-center gap-4">
            <ShoppingBag size={36} strokeWidth={1.3} className="text-gold animate-float" />
            <div>
              <h3 className="font-serif text-3xl">{t.takeawayTitle}</h3>
              <p className="text-cream/75 font-light">{t.takeawayText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* GUTSCHEINE */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <Overline className="!flex justify-center">Verschenke Genuss</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink">Geschenkgutscheine</h2>
            <p className="text-ink-soft font-light mt-4 max-w-xl mx-auto">Eine Einladung zu einem Abend voller italienischer Aromen – das perfekte Geschenk für jeden Anlass.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Cake, label: "Geburtstag", text: "Feiern bei einem Festessen unter Freunden." },
              { icon: Heart, label: "Jubiläum", text: "Ein romantischer Abend zu zweit." },
              { icon: Gift, label: "Weihnachten", text: "Eine geschmackvolle Geschenkidee zum Fest." },
            ].map((o, i) => (
              <motion.div key={o.label} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1, ease }} className="bg-sand border border-gold/20 p-8 text-center group" data-testid={`gift-${i}`}>
                <o.icon className="mx-auto text-wine mb-4 transition-transform duration-500 group-hover:scale-110" size={30} strokeWidth={1.3} />
                <h3 className="font-serif text-2xl text-ink mb-2">{o.label}</h3>
                <p className="text-ink-soft font-light text-sm">{o.text}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/gutschein" data-testid="gift-cta" className="btn-pop bg-wine text-cream hover:bg-wine-dark px-10 py-4 uppercase text-xs tracking-[0.25em] inline-block">
              Zu den Gutscheinen
            </Link>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative py-28 md:py-36 grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={img.gallery1} alt="Innenraum" className="w-full h-full object-cover animate-kenburns-slow" />
          <div className="absolute inset-0 bg-ink/75" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <Overline light className="!flex justify-center">Wir freuen uns auf Sie</Overline>
          <h2 className="font-serif font-light text-4xl sm:text-6xl text-cream mb-6 leading-tight">{t.ctaTitle}</h2>
          <p className="text-cream/75 font-light text-lg mb-10 max-w-xl mx-auto">{t.ctaText}</p>
          <a href={RESERVE_URL} target="_blank" rel="noreferrer" data-testid="cta-reserve-btn" className="btn-pop bg-gold text-ink hover:bg-gold-dark px-10 py-4 uppercase text-xs tracking-[0.25em] inline-block">Jetzt reservieren</a>
        </div>
      </section>
    </div>
  );
}
